// Morrow — moving a workflow around: duplicate (server-side copy), export (download a
// JSON file), import (hand that file back).
//
// An export is a plain JSON file the user keeps on disk, so the import side cannot trust
// it: a hand-picked .json is just as likely to be a theme export, a package.json, or a
// half-written download. The two things that must stay honest live here, away from React,
// so they can be tested on their own — deciding whether a picked file really IS one of our
// exports, and naming the file we hand out.
//
// The server owns the export's contents (GET /export returns the whole file), so nothing
// here reconstructs it; these types describe only as much as the UI has to read.

import {
  WORKFLOW_EXPORT_APP,
  WORKFLOW_EXPORT_VERSION as WORKFLOW_EXPORT_FORMAT_VERSION,
} from "@t3tools/contracts";

// The app stamp and format version come from @t3tools/contracts, shared with the
// server's exporter/importer. Keeping a second copy here is how a version bump
// silently breaks one side: the browser would reject a file the server writes.
export {
  WORKFLOW_EXPORT_APP,
  WORKFLOW_EXPORT_VERSION as WORKFLOW_EXPORT_FORMAT_VERSION,
} from "@t3tools/contracts";

/**
 * What the exporter changed on the way out. Counts, not content.
 *
 * Only three of these are things the file does NOT contain. `appEventTriggers` and
 * `vaultFolderTriggers` are counts of triggers that ARE carried and ARE recreated on
 * import — what was stripped is the account-specific binding inside them (a connected
 * account id, a vault folder id). Listing those as removed would tell the user a trigger
 * is missing when it is sitting right there in their new workflow; the server's `warnings`
 * are what cover them, in prose, so `removedEntries` deliberately ignores them.
 */
export type WorkflowExportDropped = {
  vaultFiles?: unknown;
  appEventTriggers?: unknown;
  contactTriggers?: unknown;
  vaultFolderTriggers?: unknown;
  webhookUrls?: unknown;
};

export type WorkflowExportFile = {
  formatVersion: number;
  exportedAt?: string;
  app?: string;
  workflow: Record<string, unknown> & { name?: string };
  triggers?: readonly unknown[];
  dropped?: WorkflowExportDropped;
};

/**
 * Why a picked file was refused. A code rather than a sentence: the message is localized,
 * and the three cases need genuinely different wording — a corrupt file is the user's to
 * fix, a theme export is the wrong file, and a v2 export needs a newer app.
 */
export type WorkflowExportRejection = "unreadable" | "not-an-export" | "unsupported-version";

export type ParsedWorkflowExport =
  | { ok: true; file: WorkflowExportFile }
  | { ok: false; reason: WorkflowExportRejection };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Read the text of a picked file as a workflow export.
 *
 * This refuses only what the server would also refuse — a check here that the importer
 * does not make would reject a file the server was willing to take. So a MISSING `app`
 * stamp passes (the server treats it as unstamped rather than foreign); only a stamp
 * naming someone else is fatal.
 *
 * Order matters: "unsupported version" is the last thing considered, and only once the
 * file otherwise looks like one of ours. A foreign file carrying its own `version: 2`
 * would otherwise be reported as an export from the future and send the user off to
 * update an app that is already current.
 */
export function parseWorkflowExportFile(text: string): ParsedWorkflowExport {
  let value: unknown;
  try {
    value = JSON.parse(text) as unknown;
  } catch {
    return { ok: false, reason: "unreadable" };
  }
  if (!isRecord(value)) return { ok: false, reason: "unreadable" };
  const app = typeof value.app === "string" ? value.app.trim() : "";
  if (app.length > 0 && app !== WORKFLOW_EXPORT_APP) return { ok: false, reason: "not-an-export" };
  if (!isRecord(value.workflow)) return { ok: false, reason: "not-an-export" };
  if (typeof value.formatVersion !== "number" || !Number.isFinite(value.formatVersion)) {
    return { ok: false, reason: "not-an-export" };
  }
  if (value.formatVersion !== WORKFLOW_EXPORT_FORMAT_VERSION) {
    return { ok: false, reason: "unsupported-version" };
  }
  return { ok: true, file: value as unknown as WorkflowExportFile };
}

/**
 * A workflow name as a filename segment. Same shape as the plan-markdown sanitizer
 * (`proposedPlan.ts`), plus a length cap: workflow names are free text and a 300-character
 * one would produce a filename some filesystems refuse outright.
 */
export function slugifyWorkflowName(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[`'".,!?()[\]{}]+/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
    .replace(/-+$/g, "");
  return slug.length > 0 ? slug : "workflow";
}

export function workflowExportFilename(name: string): string {
  return `morrowos-workflow-${slugifyWorkflowName(name)}.json`;
}

/**
 * The `dropped` fields that really are absent from the file, in the order the UI lists
 * them (most surprising loss first).
 *
 * `appEventTriggers` and `vaultFolderTriggers` are left out on purpose — see
 * `WorkflowExportDropped`. They are carried, not removed, and the server's `warnings`
 * already say what to do about them.
 */
export const WORKFLOW_REMOVED_KEYS = [
  "vaultFiles",
  "contactTriggers",
  "webhookUrls",
] as const satisfies ReadonlyArray<keyof WorkflowExportDropped>;

export type WorkflowRemovedKey = (typeof WORKFLOW_REMOVED_KEYS)[number];

/** How many things one `dropped` field stands for, whether the server sent a count or a list. */
export function droppedCount(value: unknown): number {
  if (typeof value === "number") return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
  if (Array.isArray(value)) return value.length;
  if (isRecord(value)) return Object.keys(value).length > 0 ? 1 : 0;
  return value ? 1 : 0;
}

/** What the file genuinely does not contain, in display order — the import notice's "not
 *  included" list. Empty means nothing was stripped, not that nothing needs attention. */
export function removedEntries(
  dropped: WorkflowExportDropped | undefined,
): ReadonlyArray<{ key: WorkflowRemovedKey; count: number }> {
  if (!dropped) return [];
  return WORKFLOW_REMOVED_KEYS.map((key) => ({ key, count: droppedCount(dropped[key]) })).filter(
    (entry) => entry.count > 0,
  );
}

/**
 * Hand the export file to the browser as a download — the `downloadPlanAsTextFile` pattern
 * (`proposedPlan.ts`), pretty-printed because this one is meant to be opened and read.
 */
export function downloadWorkflowJson(name: string, json: unknown): void {
  const blob = new Blob([`${JSON.stringify(json, null, 2)}\n`], {
    type: "application/json;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = workflowExportFilename(name);
  anchor.click();
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 0);
}
